import { useCallback } from 'react';
import { v4 } from 'uuid';
import { pm2SitemapStore, findPageDescriptorByPath } from '@pm2/sitemap-utils';
import { pm2ConfigStore } from '@pm2/store';
import { useLocation, useNavigationManager } from '@provider/navigation';
import { useNotifier } from '@provider/notifier';
import { useUserIdentityState } from '@provider/user-identity';
import { useCreateRevisionMutation } from '../api/mutation.create.js';
import { useUpdateRevisionMutation } from '../api/mutation.update.js';
import { createRevisionModalConfig } from '../modals/create.js';
import { makeUpdateRevisionModalConfig } from '../modals/update.js';
import { useCourseRevisionState, courseRevisionsStore } from '../store.js';
import { getFirstValidChapterDescriptorInCourse } from '../util.get-first-valid-chapter-descriptor-in-course.js';
import { makeShareRevisionModalConfig } from '../modals/share/modal.js';

function useSaveRevision(courseDescriptor) {
  const portalId = pm2ConfigStore.useSelector(state => state.config.id);
  const sitemapState = pm2SitemapStore.useSelector(state => state);
  const location = useLocation();
  const notifier = useNotifier();
  const user = useUserIdentityState(state => state.user);
  const navigation = useNavigationManager();
  const editedRevision = useCourseRevisionState(state => state.editableRevision);
  const createMutation = useCreateRevisionMutation();
  const updateMutation = useUpdateRevisionMutation();
  const chapterDescriptor = findPageDescriptorByPath(sitemapState, location.pathname);
  return useCallback(async () => {
    if (!courseDescriptor) {
      return;
    }
    const newDisabledIds = courseRevisionsStore.getState().disabledIds;

    //
    // STEP: Resolve the revision that should be saved, including relevant
    // metadata.
    //
    const nextRevision = await (async () => {
      if (editedRevision) {
        switch (await notifier.modal(makeUpdateRevisionModalConfig(editedRevision))) {
          case "save":
            return {
              method: "update",
              revision: {
                ...editedRevision,
                disabledContentIds: newDisabledIds
              }
            };
          case "create-new":
            {
              const newModalConfig = await notifier.modal(createRevisionModalConfig);
              if (!newModalConfig) {
                return undefined;
              }
              const id = v4();
              return {
                method: "create",
                share: newModalConfig.intent === "save-and-share",
                revision: {
                  id,
                  __clientId: id,
                  __syncedWithBackend: false,
                  isActive: true,
                  courseId: courseDescriptor.id,
                  portalId,
                  userId: user?.id ?? "",
                  name: newModalConfig.title,
                  disabledContentIds: newDisabledIds
                }
              };
            }
        }
      } else {
        const id = v4();
        const newModalConfig = await notifier.modal(createRevisionModalConfig);
        if (!newModalConfig) {
          return undefined;
        }
        return {
          method: "create",
          share: newModalConfig.intent === "save-and-share",
          revision: {
            id,
            __clientId: id,
            __syncedWithBackend: false,
            isActive: true,
            courseId: courseDescriptor.id,
            portalId,
            userId: user?.id ?? "",
            name: newModalConfig.title,
            disabledContentIds: newDisabledIds
          }
        };
      }
    })();
    if (!nextRevision) {
      // If we get here the user cancelled the save flow and as such it is safe
      // to bail out.
      return;
    }

    //
    // STEP: Perform save including relevant side effects such as navigation,
    // etc.
    //
    const firstValidChapter = getFirstValidChapterDescriptorInCourse(nextRevision.revision, courseDescriptor);
    const applyRevision = () => {
      switch (nextRevision.method) {
        case "create":
          createMutation.mutate(nextRevision.revision);
          break;
        case "update":
          updateMutation.mutate(nextRevision.revision);
      }
      courseRevisionsStore.dispatch(courseRevisionsStore.actions.toggleEditMode({
        state: false
      }));
    };

    // In case the chapter that the user is currently on has been removed in
    // the most recent revision, then automatically navigate them to the
    // nearest chapter that is still present.
    if (chapterDescriptor && newDisabledIds[chapterDescriptor.id]?.disabled && firstValidChapter) {
      // ... and ensure that the navigation has been fully completed before
      // applying the revision to avoid flashes of content before navigation
      // ends.
      const unsubscribe = navigation.subscribe(() => {
        const location = navigation.getLocation();
        if (location.pathname === firstValidChapter.path) {
          applyRevision();
          unsubscribe();
        }
      });
      navigation.replace(firstValidChapter.path);
    } else {
      applyRevision();
    }
    if (nextRevision.share) {
      await notifier.modal(makeShareRevisionModalConfig(nextRevision.revision, {
        persistOnNavigation: true
      }));
    }
  }, [chapterDescriptor, courseDescriptor, createMutation, editedRevision, navigation, notifier, portalId, updateMutation, user?.id]);
}

export { useSaveRevision };
//# sourceMappingURL=save.js.map
